// packages/core/src/learning/school-modules/minimalism.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Minimalism". surface: 'school', homeSchool: 'minimalism'.
// module id 'school-minimalism' matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #3ec9b8) — flag for the presentation-tuning pass.
// tier/pillar: consumption/spending-control lens in the freedom-lifestyle-paths
// bucket → tier 'control', pillar 'cash-flow-control' (mirrors the frugality
// mapping; both are lower-needs/spending lenses rather than projection-math).
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_MINIMALISM_MODULE: LearningModule = {
	id: 'school-minimalism',
	title: 'Minimalism',
	description:
		'The lens that treats every object, habit, and commitment as a carrying cost — reducing what you maintain to free up money, attention, and peace without sliding into purity or performance.',
	emoji: '⬜', // PLACEHOLDER — tune in presentation pass
	accentColor: '#3ec9b8', // from SchoolMeta color — tune in presentation pass
	tier: 'control',
	pillar: 'cash-flow-control',
	order: 0,
	surface: 'school',
	homeSchool: 'minimalism',
	lessons: [
		{
			id: 'school-minimalism-01',
			title: 'Less can create more room',
			description:
				'This lesson teaches the core minimalism lens: reducing what you carry can create more space for money, attention, and life.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['minimalism'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['carrying-cost', 'attention-space', 'enough-point'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['carrying cost', 'attention space', 'enough point'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Minimalism gets misunderstood as empty walls, tiny closets, and pretending you do not want anything. The stronger version is more practical: what is your life carrying that no longer deserves space?',
				},
				{
					type: 'paragraph',
					text: 'Minimalism is paying attention to carrying cost. Every object, habit, subscription, and commitment asks for money, time, storage, cleaning, decisions, attention, and emotional space.',
				},
				{
					type: 'bullet_list',
					items: [
						'Less to buy',
						'Less to maintain',
						'Less to think about',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking minimalism is about owning the fewest things instead of carrying fewer burdens.',
				},
				{
					type: 'example',
					text: 'A closet full of clothes you barely wear does not only cost the purchase price. It costs space, decisions, guilt, and the feeling that you still need more.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that less is not automatically better. Some things support your work, family, culture, hobbies, safety, health, or joy. Minimalism breaks when it treats all possession as weakness.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your savings rate and runway. You are not trying to erase your life. You are asking what could become lighter and what that would give back.',
				},
				{
					type: 'key_takeaway',
					text: 'Minimalism works when less carrying creates more room to live.',
				},
			],
			audioScript:
				'Minimalism gets flattened into an aesthetic. White rooms, empty shelves, perfect closets, one chair, one mug, one plant, and no visible life. That version is easy to mock because most people do not actually want to live like a showroom.\n\nThe useful version of minimalism is not about owning almost nothing. It is about carrying less of what no longer serves you.\n\nThat word matters: carrying. Everything you own, schedule, subscribe to, store, clean, maintain, organize, insure, move, repair, and think about has a carrying cost. The cost is not only money. It is attention. It is time. It is space. It is decision fatigue. It is guilt. It is the quiet pressure of having a life that keeps asking to be managed.\n\nMinimalism notices that a crowded life can still look normal. A crowded closet. A crowded phone. A crowded schedule. A crowded budget. A crowded apartment. A crowded mind. You can have a lot and still feel like nothing is enough. You can keep buying solutions to a life that mostly needs subtraction.\n\nThink about a closet full of clothes you rarely wear. The cost was not only the original purchase. Now you have to store them. Sort them. Feel bad about not wearing them. Decide around them. Maybe buy more because somehow the closet is full but still does not feel right. That is carrying cost.\n\nThe minimalist lens asks: what would happen if the goal was not more, but clearer?\n\nThis does not mean every possession is bad. That is where minimalism can become silly or rigid. Some things matter. Tools matter. Family items matter. Cultural items matter. Hobbies matter. Comfort matters. Beauty matters. A home that feels warm matters. Minimalism is not supposed to turn you into a person who owns nothing and judges everyone who owns more.\n\nThe mature version is about enough. What is enough clothing for your real life? Enough space? Enough commitments? Enough subscriptions? Enough upgrades? Enough digital noise? Enough social obligations? Once you know enough, you stop letting the world sell you a new identity every week.\n\nInside Nisk, this lens connects to money because lower needs can create margin. Fewer automatic purchases can strengthen your savings rate. Fewer fixed obligations can improve runway. Fewer wants can make income feel more powerful. But the real win is bigger than the math. A lighter life can create more attention for the things you actually want to build.\n\nMinimalism works when it gives you room. Not when it becomes another rule to obey.',
			flashcards: [
				{
					id: 'school-minimalism-01-fc-1',
					role: 'main_idea',
					front: 'What is carrying cost?',
					back: 'The money, time, attention, and energy something keeps demanding.',
				},
				{
					id: 'school-minimalism-01-fc-2',
					role: 'watch_out',
					front: 'What is fake minimalism?',
					back: 'Chasing the fewest things instead of carrying fewer burdens.',
				},
				{
					id: 'school-minimalism-01-fc-3',
					role: 'use_it',
					front: 'How do you apply it?',
					back: 'Remove what costs more than it gives back.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Find one carrying cost',
				prompt: 'Write one thing in your life that costs more attention, money, or energy than it gives back.',
			},
			revision: 1,
		},
		{
			id: 'school-minimalism-02',
			title: 'Wanting less changes the math',
			description:
				'This lesson teaches how minimalism lowers pressure by reducing the lifestyle that your income has to support.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['minimalism'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['lower-needs', 'lifestyle-pressure', 'enough-math'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['lower needs', 'lifestyle pressure', 'enough math'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Most money plans focus on getting more. More income, more assets, more upgrades, more choices. Minimalism asks a quieter question: what if wanting less made the whole system easier?',
				},
				{
					type: 'paragraph',
					text: 'This lens believes lower needs can change your money math. If your life requires less to feel good, your income has more power and your freedom target can become less heavy.',
				},
				{
					type: 'bullet_list',
					items: [
						'Fewer wants reduce pressure',
						'Lower fixed costs create room',
						'Enough makes raises more useful',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is raising your needs every time your income rises.',
				},
				{
					type: 'example',
					text: 'A raise can disappear into a bigger apartment, more subscriptions, new payments, and constant upgrades. Or part of it can become breathing room because your needs did not grow as fast.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that wanting less is not the same as needing less. Some costs are real, especially housing, healthcare, transportation, childcare, debt, and family support.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens belongs in savings rate and runway testing. You can ask what changes if your required lifestyle becomes lighter without pretending every cost is optional.',
				},
				{
					type: 'key_takeaway',
					text: 'Minimalism changes the math by lowering the life your money has to fund.',
				},
			],
			audioScript:
				'Most money advice tells you to get more. More income. More investments. More assets. More productivity. More opportunity. More everything. Some of that matters. Earning more can be powerful. Building assets can be powerful. But minimalism adds a different question: what if wanting less also changed the game?\n\nThis does not mean giving up ambition. It means noticing that your financial life is shaped by what your lifestyle requires. If your life needs constant upgrades to feel acceptable, your income has to run harder. If every raise becomes a nicer apartment, newer car, more subscriptions, more clothes, more gadgets, and more expensive habits, the pressure does not drop. You may earn more and still feel trapped.\n\nMinimalism sees wanting as part of the math.\n\nIf you need less to feel good, your money can do more. A smaller lifestyle can make a normal income feel less suffocating. Fewer fixed costs can create runway. Less shopping can create margin. Fewer status purchases can make a raise matter. Lower needs can make freedom less far away.\n\nThis is not about pretending life is cheap. It is not. Rent can be brutal. Healthcare can be expensive. Transportation can be necessary. Childcare can be heavy. Debt can squeeze the whole system. Supporting family can change everything. Minimalism should never pretend that all costs are optional or that people struggle only because they want too much.\n\nThe distinction is between needs, values, and noise. Needs are real. Values are worth funding. Noise is the stuff you keep buying, upgrading, storing, and maintaining without a strong reason. Minimalism tries to cut the noise so the real needs and real values have more room.\n\nImagine someone gets a raise. The default culture says, upgrade. Better apartment. Newer car. More convenience. More proof that you are doing well. Minimalism says, pause. Maybe some upgrade is worth it. But maybe part of that raise should become breathing room. Maybe it should become a smaller work trap. Maybe it should become runway. Maybe it should become the ability to say no later.\n\nInside Nisk, this is where savings rate and runway matter. You can test what happens when required monthly spending is lighter. Not because lower spending is always the answer, but because it reveals the tradeoff. What does your life cost to maintain? What would happen if that cost stopped rising with every income improvement?\n\nWanting less changes the math because the finish line gets less heavy. Your money no longer has to fund every version of you that advertising tries to create.',
			flashcards: [
				{
					id: 'school-minimalism-02-fc-1',
					role: 'main_idea',
					front: 'How does wanting less help?',
					back: 'It lowers the lifestyle your income has to support.',
				},
				{
					id: 'school-minimalism-02-fc-2',
					role: 'watch_out',
					front: 'What is the upgrade trap?',
					back: 'Raising your needs every time your income rises.',
				},
				{
					id: 'school-minimalism-02-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'Test how lower required spending changes margin and runway.',
				},
			],
			applyAction: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
			revision: 1,
		},
		{
			id: 'school-minimalism-03',
			title: 'Where minimalism becomes purity',
			description:
				'This lesson teaches the limits of minimalism and why simplicity should not become judgment, deprivation, or avoidance.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['minimalism'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['simplicity-trap', 'purity-mindset', 'human-enough'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['simplicity trap', 'purity mindset', 'human enough'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Minimalism can make life lighter, but it can also become another performance. If simplicity turns into a purity contest, the lens starts working against you.',
				},
				{
					type: 'paragraph',
					text: 'This lens breaks down when it treats less as morally superior. Owning fewer things does not automatically make someone wiser, calmer, richer, or more disciplined.',
				},
				{
					type: 'bullet_list',
					items: [
						'Less is not always better',
						'Simple is not always fair',
						'Enough is personal',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is turning minimalism into identity instead of using it as a tool.',
				},
				{
					type: 'example',
					text: 'A person with children, roommates, elders, tools, cultural items, or medical needs may own more because real life asks for more. That is not failure.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is avoidance. Sometimes removing things is easier than facing income problems, relationship stress, career uncertainty, or emotional discomfort.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, the mature move is to ask what simplicity is giving back. If it gives back money, peace, time, and attention, it is working. If it gives back pride and fear, check the lens.',
				},
				{
					type: 'key_takeaway',
					text: 'Minimalism should make your life lighter, not make your identity smaller.',
				},
			],
			audioScript:
				'Minimalism is powerful because it gives people permission to remove what is not working. But there is a point where it can turn into another kind of pressure.\n\nAt first, removing things can feel freeing. You clean out the closet. Cancel subscriptions. Buy less. Stop saying yes to every plan. Clear your space. Notice that you feel calmer. That can be real. The problem starts when less becomes a purity contest.\n\nNow you are not just simplifying. You are proving something. You start measuring yourself by how little you own, how little you need, how little you spend, how clean your space looks, or how different you are from people who buy more. That is not freedom. That is ego in a smaller room.\n\nMinimalism breaks down when it treats less as automatically better. Less can be better, but not always. A family may need more stuff than a single person. Someone with medical needs may need more supplies. Someone with a trade, hobby, or creative life may need tools. Someone who lives with elders, children, roommates, or extended family may not control every object in the home. Someone’s culture may value objects, gifts, clothing, gatherings, and shared possessions differently.\n\nMinimalism should not shame real life.\n\nThe question is not, how little can I own? The better question is, what helps this life work? What supports health, family, work, culture, joy, safety, and peace? What is just noise? What keeps asking for attention but gives little back?\n\nThere is another blind spot too. Sometimes decluttering becomes avoidance. It is easier to clean a drawer than face a career problem. Easier to cancel a subscription than talk about relationship stress. Easier to make your room look simple than admit your income is too low, your rent is too high, or your schedule is breaking you. Removing things can help, but it cannot solve every deeper issue.\n\nThat does not make minimalism weak. It makes it a tool. A tool should serve the job. If simplicity gives you more money, more peace, more attention, more time, and more honesty, it is working. If simplicity makes you rigid, proud, scared to spend, or judgmental, the tool is taking over.\n\nInside Nisk, this lens should lead to reflection, not performance. What are you removing, and what are you getting back? Is your life becoming lighter, or are you just trying to become the kind of person who looks minimal? Are you choosing enough, or chasing emptiness?\n\nMinimalism is at its best when it helps you live more clearly. It is at its worst when it becomes another identity you have to maintain.',
			flashcards: [
				{
					id: 'school-minimalism-03-fc-1',
					role: 'main_idea',
					front: 'Where can minimalism fail?',
					back: 'When less becomes identity, judgment, or performance.',
				},
				{
					id: 'school-minimalism-03-fc-2',
					role: 'watch_out',
					front: 'What is the purity trap?',
					back: 'Believing owning less makes you morally better.',
				},
				{
					id: 'school-minimalism-03-fc-3',
					role: 'use_it',
					front: 'What should you ask?',
					back: 'Ask what simplicity gives back in money, time, peace, or attention.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Check your simplicity',
				prompt: 'Write whether simplifying is giving you peace, money, time, attention, or just pride.',
			},
			revision: 1,
		},
	],
};
